/**
 * Insights — Agents & Knowledge tab
 *
 * Honest version. Real data: /insights-module/top-agents and
 * /insights-module/knowledge. Tool-call stats, document retrieval
 * rankings, unused knowledge, freshness pie, growing topics, and
 * low-confidence clusters have no backend yet — rendered as
 * empty-state placeholders.
 *
 * Backend gaps tracked separately — see MOCK-INVENTORY-2026-05-06.md.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Bot, Wrench, BookOpen, FileWarning, Sparkles, TrendingUp,
  AlertTriangle, FileX, BarChart3,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend,
} from 'recharts';
import { CHART_TOOLTIP } from '@/lib/chart-theme';
import { Progress } from '@/components/ui/progress';
import {
  KpiCard, ChartCard, FilterBar, ActionItemsDrawer, EmptySection,
  filtersToParams, rangeLabel, rangeEmptyStatePhrase,
  useInsightsSearchFilters, validateInsightsSearch,
} from '@/components/insights';
import { useAgents } from '@/lib/use-agents';
import {
  useTopAgents,
  useInsightsKnowledge,
  useInsightsSummary,
} from '@/lib/use-insights';

export const Route = createFileRoute('/insights/agents-knowledge')({
  component: AgentsKnowledgeTab,
  // INS-FILTER-PERSIST-001 — URL-bind filter state per resolved decision #9.
  validateSearch: validateInsightsSearch,
});

function AgentsKnowledgeTab() {
  const [filters, setFilters] = useInsightsSearchFilters('/insights/agents-knowledge');
  const [actionItemsOpen, setActionItemsOpen] = useState(false);

  // R3-P0 defect #1 — FilterBar filters propagate to backend.
  const filterParams = filtersToParams(filters);
  // Pod-KK #111 — labels follow the FilterBar range.
  const rl = rangeLabel(filters.range);
  // Bug #1117 — full-sentence range phrase for empty-state copy.
  const rp = rangeEmptyStatePhrase(filters.range);
  const { data: agents = [], isLoading: agentsLoading } = useTopAgents(filterParams);
  // Resolve agentId from query_metrics to a real custom_agents row.
  // Some seed agentIds (e.g., insights-seed-compliance) don't exist as agents
  // — for those we render a non-link badge instead of a broken /agents/$id link.
  const { data: realAgents = [] } = useAgents('all');
  // Insights bug #5 — system agents (Ask Argus + the other seeded system
  // agents) are keyed in query_metrics by their SLUG (`system.ask_argus`,
  // `ask-argus`, `system.*`), not by a UUID, and they live in `agents`
  // with org_id IS NULL so they never appear in /custom-agents (the source
  // of `realAgents`). Pre-fix the #1 agent by volume (Ask Argus, 941
  // queries) was flagged "(no agent record)" with drill-down disabled.
  // Treat a known system slug as a real, drillable agent — the detail page
  // resolves it via the new GET /custom-agents/by-slug/:slug path.
  // Only GENUINE system slugs are marked drillable here: `system.*`
  // (canonical seeded agents) + the legacy `ask-argus` alias. These are
  // guaranteed to resolve via /custom-agents/by-slug. We deliberately do
  // NOT guess at arbitrary seed slugs (e.g. `insights-seed-compliance`)
  // that have no agents-table row — those keep the honest
  // "(no agent record)" disabled state rather than a broken drill-down.
  const isSystemAgentSlug = (id: string | null | undefined): boolean =>
    !!id && (id.startsWith('system.') || id === 'ask-argus');
  const agentExists = (id: string | null | undefined): boolean =>
    !!id && (realAgents.some(a => a.id === id) || isSystemAgentSlug(id));
  const { data: knowledge, isLoading: knowledgeLoading } = useInsightsKnowledge(filterParams);
  // Bug — the KPI numbers must come from the authoritative /summary
  // aggregate, NOT the LIMIT-20 top-agents list (which truncated both the
  // agent count and the run total, contradicting the Overview tab).
  const { data: summary, isLoading: summaryLoading } = useInsightsSummary(filterParams);

  const totalAgentRuns = summary?.totalQueries ?? 0;
  const uniqueAgents = summary?.uniqueAgents30d ?? 0;
  const totalDocs = knowledge?.documents ?? 0;
  const totalCollections = knowledge?.collections ?? 0;
  const queriesWithRag = knowledge?.queriesWithRag30d ?? 0;
  const queriesWithoutRag = knowledge?.queriesWithoutRag30d ?? 0;
  const totalRagSplit = queriesWithRag + queriesWithoutRag;
  const ragHitPct = totalRagSplit > 0 ? queriesWithRag / totalRagSplit : 0;

  const topAgentsBar = agents.slice(0, 8).map((a) => ({
    name: a.agentId ?? 'unknown',
    Queries: a.queryCount ?? 0,
    Tokens: a.totalTokens ?? 0,
  }));

  return (
    <div className="flex flex-col h-full">
      <FilterBar
        filters={filters}
        onChange={setFilters}
        actionItemsCount={0}
        onOpenActionItems={() => setActionItemsOpen(true)}
      />

      {/* Pod-KK #613 — explicit custom-range fallback notice. */}
      {filterParams.customFallback && (
        <div
          role="status"
          className="mx-6 mt-3 rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200"
        >
          Custom date ranges aren't supported yet — showing the last 7
          days. Pick a preset range to filter precisely.
        </div>
      )}

      <div className="flex-1 p-6 space-y-6">
        {/* KPIs — real */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard
            label={`Agent runs (${rl})`}
            icon={Bot}
            value={summaryLoading ? '—' : totalAgentRuns.toLocaleString()}
            numericValue={totalAgentRuns}
            subtitle={`${uniqueAgents} agents seen`}
          />
          <KpiCard
            label="Knowledge docs"
            icon={BookOpen}
            value={knowledgeLoading ? '—' : totalDocs.toLocaleString()}
            numericValue={totalDocs}
            subtitle={`${totalCollections} collections`}
          />
          <KpiCard
            label={`Queries with RAG (${rl})`}
            icon={Sparkles}
            value={knowledgeLoading ? '—' : queriesWithRag.toLocaleString()}
            numericValue={queriesWithRag}
            subtitle={
              totalRagSplit > 0
                ? `${(ragHitPct * 100).toFixed(0)}% of queries`
                : '—'
            }
          />
          <KpiCard
            label={`Queries without RAG (${rl})`}
            icon={FileWarning}
            value={knowledgeLoading ? '—' : queriesWithoutRag.toLocaleString()}
            numericValue={queriesWithoutRag}
            subtitle="rag_source_count = 0 or NULL"
          />
        </div>

        {/* Top agents — real */}
        <ChartCard title={`Top agents by volume (${rl})`} icon={BarChart3}>
          {topAgentsBar.length === 0 ? (
            <EmptySection
              message={`No agent activity ${rp}.`}
              hint="Per-agent rollup comes from /insights-module/top-agents (query_metrics grouped by agent_id)."
              minHeight="min-h-56"
            />
          ) : (
            <>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topAgentsBar} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    {/* Queries (≤~1k) and Tokens (≤~150k) are incomparable
                        magnitudes — give each its own Y-axis so the small
                        Queries bars aren't crushed to invisibility. */}
                    <YAxis yAxisId="left" tick={{ fontSize: 10 }} width={50} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} width={50} />
                    <Tooltip {...CHART_TOOLTIP} />
                    <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                    <Bar yAxisId="left" dataKey="Queries" fill="rgb(99 102 241)" />
                    <Bar yAxisId="right" dataKey="Tokens" fill="rgb(16 185 129)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5 mt-4">
                {agents.slice(0, 8).map((a) => {
                  const max = agents[0]?.queryCount ?? 1;
                  const pct = ((a.queryCount ?? 0) / max) * 100;
                  const exists = agentExists(a.agentId);
                  // Pod-KK #113 — when query_metrics surfaces a slug
                  // that no longer maps to a real agent record (e.g.
                  // the seed-only `insights-seed-compliance` rows),
                  // render an explicit "(no agent record)" hint
                  // instead of letting the user click into a broken
                  // /agents/$agentId page that just says "Agent Not
                  // Found".
                  const inner = (
                    <>
                      <Bot className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="font-medium truncate min-w-0 flex-1">
                        {a.agentId ?? 'Unknown agent'}
                        {!exists && (
                          <span className="ml-1.5 text-[10px] uppercase tracking-wide text-muted-foreground/80">
                            (no agent record)
                          </span>
                        )}
                      </span>
                      <span className="text-xs font-semibold tabular-nums w-16 text-right">
                        {(a.queryCount ?? 0).toLocaleString()}
                      </span>
                      <div className="w-24">
                        <Progress value={pct} className="h-1.5" />
                      </div>
                    </>
                  );
                  return exists ? (
                    <Link
                      key={a.agentId ?? 'unknown'}
                      to={'/agents/$agentId' as any}
                      params={{ agentId: a.agentId ?? 'unknown' } as any}
                      className="flex items-center gap-2 text-xs rounded-md p-1.5 hover:bg-muted/30 transition-colors"
                    >
                      {inner}
                    </Link>
                  ) : (
                    <div
                      key={a.agentId ?? 'unknown'}
                      className="flex items-center gap-2 text-xs rounded-md p-1.5 opacity-80 cursor-not-allowed"
                      title={
                        `This metric row references "${a.agentId ?? 'unknown'}", ` +
                        `which has no matching agent record. Likely a ` +
                        `seed-only or retired agent — drill-down disabled.`
                      }
                      aria-disabled="true"
                    >
                      {inner}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </ChartCard>

        {/* Empty-state sections for missing backends */}
        <ChartCard title="Tool call statistics (30d)" icon={Wrench}>
          <EmptySection
            message="Per-tool MCP gateway stats not yet wired into Insights."
            hint="Source data exists in MCP gateway logs; needs an aggregation endpoint."
          />
        </ChartCard>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Top retrieved documents (30d)" icon={BookOpen}>
            <EmptySection
              message="Per-document retrieval rankings not surfaced."
              hint="Needs a counter on each retrieval call keyed by document_id."
              minHeight="min-h-56"
            />
          </ChartCard>

          <ChartCard title="Knowledge freshness" icon={FileWarning}>
            <EmptySection
              message="Document freshness buckets not yet wired."
              hint="Backend has document timestamps but no aggregated freshness rollup endpoint."
              minHeight="min-h-56"
            />
          </ChartCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ChartCard title="Top growing topics" icon={TrendingUp}>
            <EmptySection
              message="Topic growth not available."
              hint="Requires NLP clustering of queries with week-over-week deltas."
            />
          </ChartCard>

          <ChartCard title="Low-confidence query clusters" icon={AlertTriangle}>
            <EmptySection
              message="Low-confidence cluster mining not available."
              hint="Requires retrieval-score persistence + clustering."
            />
          </ChartCard>

          <ChartCard title="Unused knowledge" icon={FileX}>
            <EmptySection
              message="Unused-document detection not wired."
              hint="Requires a last-retrieved-at column on documents."
            />
          </ChartCard>
        </div>
      </div>

      <ActionItemsDrawer
        open={actionItemsOpen}
        onOpenChange={setActionItemsOpen}
        items={[]}
      />
    </div>
  );
}
